/**
 * Input Validation Utilities
 * Sanitize and validate user inputs to prevent security issues
 */

/**
 * Validate quiz data before submission
 */
export function validateQuizInput(quiz) {
  if (!quiz.title || typeof quiz.title !== "string") {
    throw new Error("Quiz title is required");
  }
  if (quiz.title.trim().length === 0) {
    throw new Error("Quiz title cannot be empty");
  }
  if (quiz.title.length > 200) {
    throw new Error("Quiz title must be 200 characters or less");
  }

  if (!Array.isArray(quiz.questions) || quiz.questions.length === 0) {
    throw new Error("Quiz must have at least one question");
  }

  if (quiz.questions.length > 100) {
    throw new Error("Quiz cannot have more than 100 questions");
  }

  quiz.questions.forEach((q, i) => {
    if (!q.text || typeof q.text !== "string") {
      throw new Error(`Question ${i + 1}: Text is required`);
    }
    if (q.text.length > 1000) {
      throw new Error(`Question ${i + 1}: Text is too long (max 1000 characters)`);
    }
    if (!Array.isArray(q.options) || q.options.length < 2) {
      throw new Error(`Question ${i + 1}: Must have at least 2 options`);
    }
    if (q.options.length > 10) {
      throw new Error(`Question ${i + 1}: Cannot have more than 10 options`);
    }
    q.options.forEach((opt, j) => {
      if (!opt || typeof opt !== "string") {
        throw new Error(`Question ${i + 1}, Option ${j + 1}: Option is required`);
      }
      if (opt.length > 500) {
        throw new Error(`Question ${i + 1}, Option ${j + 1}: Option text is too long`);
      }
    });
  });

  return true;
}

/**
 * Validate file uploads (type and size)
 */
export function validateFileUpload(file, options = {}) {
  const {
    allowedTypes = ["image/jpeg", "image/png", "image/webp", "application/pdf"],
    maxSize = 5 * 1024 * 1024, // 5MB default
  } = options;

  if (!file) {
    throw new Error("No file selected");
  }

  if (!allowedTypes.includes(file.type)) {
    throw new Error(
      `File type not allowed. Allowed types: ${allowedTypes.join(", ")}`
    );
  }

  if (file.size > maxSize) {
    throw new Error(
      `File size exceeds limit. Maximum size: ${(maxSize / 1024 / 1024).toFixed(1)}MB`
    );
  }

  return true;
}

/**
 * Sanitize string input (remove potentially dangerous characters)
 */
export function sanitizeInput(input) {
  if (typeof input !== "string") return "";

  return input
    .trim()
    .replace(/[<>\"']/g, "") // Remove angle brackets and quotes
    .replace(/javascript:/gi, "") // Remove javascript: protocol
    .replace(/on\w+\s*=/gi, "") // Remove event handlers
    .replace(/script/gi, ""); // Remove script tags
}

/**
 * Validate and sanitize user profile data
 */
export function validateUserProfile(profile) {
  if (!profile || typeof profile !== "object") {
    throw new Error("Invalid profile data");
  }

  if (profile.name && typeof profile.name === "string") {
    if (profile.name.length > 100) {
      throw new Error("Name is too long (max 100 characters)");
    }
  }

  if (profile.email && typeof profile.email === "string") {
    validateEmail(profile.email);
  }

  if (profile.batch && !["C1", "C2", "Both"].includes(profile.batch)) {
    throw new Error("Invalid batch value");
  }

  return true;
}

/**
 * Validate classroom data
 */
export function validateClassroomData(classroom) {
  if (!classroom || typeof classroom !== "object") {
    throw new Error("Invalid classroom data");
  }

  if (!classroom.name || typeof classroom.name !== "string") {
    throw new Error("Classroom name is required");
  }

  if (classroom.name.length > 150) {
    throw new Error("Classroom name is too long");
  }

  if (classroom.description && typeof classroom.description === "string") {
    if (classroom.description.length > 1000) {
      throw new Error("Description is too long (max 1000 characters)");
    }
  }

  if (!["PUC-I", "PUC-II", "Engineering Physics"].includes(classroom.level)) {
    throw new Error("Invalid classroom level");
  }

  return true;
}

/**
 * Validate email format
 */
export function validateEmail(email) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    throw new Error("Invalid email format");
  }
  return true;
}

/**
 * Validate URL (basic check)
 */
export function validateUrl(url) {
  try {
    new URL(url);
    return true;
  } catch {
    throw new Error("Invalid URL format");
  }
}

/**
 * Validate text length
 */
export function validateLength(text, minLength = 1, maxLength = 500) {
  if (!text || text.length < minLength) {
    throw new Error(`Text must be at least ${minLength} characters`);
  }
  if (text.length > maxLength) {
    throw new Error(`Text must be ${maxLength} characters or less`);
  }
  return true;
}

/**
 * Rate limiting helper - returns true if action is allowed
 */
export function checkRateLimit(key, maxAttempts = 5, timeWindowMs = 60000) {
  const storage = window.__rateLimitMap || {};
  window.__rateLimitMap = storage;

  const now = Date.now();
  const record = storage[key] || { attempts: 0, resetTime: now + timeWindowMs };

  // Check if window has expired
  if (now > record.resetTime) {
    storage[key] = { attempts: 1, resetTime: now + timeWindowMs };
    return true;
  }

  // Increment attempts
  record.attempts += 1;

  if (record.attempts > maxAttempts) {
    throw new Error(
      `Too many attempts. Please wait ${Math.ceil((record.resetTime - now) / 1000)} seconds.`
    );
  }

  return true;
}

export default {
  validateQuizInput,
  validateFileUpload,
  sanitizeInput,
  validateEmail,
  validateUrl,
  validateLength,
  checkRateLimit,
  validateUserProfile,
  validateClassroomData,
};
